import { ErrorContext } from "../context/ErrorContext";
import { useContext, useEffect, useMemo, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { createAxiosInstance } from "../api/axios";
import {
    Box,
    Typography,
    Button,
    Container,
    Card,
    CardContent,
    Alert,
    CircularProgress,
} from '@mui/material';
import { AuthContext } from "../context/AuthContext";
import PersonalInfoForm from "./ManageSelf";
import { hasCompletedPersonalInfo } from "../utils/profileValidation";
import GradeScoreDisplay from './GradeScoreDisplay';
import { ProfileCompletionModal } from './JobDrawer';

const JobApply = () => {
    const { id } = useParams();  // Get job ID from URL
    const { setError } = useContext(ErrorContext);
    const { user, loading } = useContext(AuthContext);
    const [submitted, setSubmitted] = useState(false);
    const [alreadyApplied, setAlreadyApplied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [jobDetails, setJobDetails] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [rankingResults, setRankingResults] = useState(null);
    const [noResumeError, setNoResumeError] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Loading job details...");

    const authAxios = useMemo(() => {
        return createAxiosInstance(null);
    }, []);

    // Submit application with resume
    const submitApplication = useCallback(async (resumeId, jobData) => {
        const payload = {
            job_uuid: id,
            user_resume: resumeId
        };
        
        try {
            // Step 1: Submit application
            setLoadingMessage("Submitting your application...");
            const applicationResponse = await authAxios.post('/application/', payload);
            const applicationData = applicationResponse.data;
            
            // Show analyzing state
            setIsLoading(false);
            setIsAnalyzing(true);
            
            try {
                // Step 2: Get job details for description
                const jobDescription = JSON.stringify(jobData);
                
                // Step 3: Call resume ranking API
                const rankingPayload = {
                    resume_id: applicationData.user_resume.id,
                    application_uuid: applicationData.uuid,
                    job_uuid: applicationData.job.uuid,
                    job_description: jobDescription
                };
                
                const rankingResponse = await authAxios.post('/resume-ranking/', rankingPayload);
                
                // Store ranking results for display
                setRankingResults(rankingResponse.data);
                
                // Step 4: Show success only after all steps complete
                setIsAnalyzing(false);
                setSubmitted(true);
                
            } catch (rankingError) {
                console.error('Resume ranking failed:', rankingError);
                setIsAnalyzing(false);
                // Still show success for application submission even if ranking fails
                setSubmitted(true);
            }
            
        } catch (error) {
            setIsLoading(false);
            if (error.response && error.response.data && error.response.data.Error && Array.isArray(error.response.data.Error)) {
                if (error.response.data.Error[0].toLowerCase().includes('already applied')) {
                    setAlreadyApplied(true);
                    return;
                }
            }
            setError('Failed to submit application');
        }
    }, [id, authAxios, setError]);

    useEffect(() => {
        const initializeAndApply = async () => {
            if (!user || loading) return;
            
            setIsLoading(true);
            
            try {
                // Step 1: Fetch job details
                setLoadingMessage("Loading job details...");
                const jobResponse = await authAxios.get(`/job/${id}/`);
                const jobData = jobResponse.data;
                setJobDetails(jobData);
                
                // Step 2: Check if already applied
                setLoadingMessage("Checking application status...");
                try {
                    const appResponse = await authAxios.get('/application/', { params: { job_uuid: id } });
                    const applications = appResponse.data.results || [];
                    if (applications.some(app => app.applicant && app.applicant.id === user.id)) {
                        setAlreadyApplied(true);
                        setIsLoading(false);
                        return;
                    }
                } catch (error) {
                    console.error('Failed to check existing applications:', error);
                }
                
                // Step 3: Get user's latest resume
                setLoadingMessage("Finding your latest resume...");
                const userResumes = user?.resumes || [];
                
                if (userResumes.length === 0) {
                    setNoResumeError(true);
                    setIsLoading(false);
                    return;
                }
                
                // Get the latest resume (last one in the array or by highest ID)
                const latestResume = userResumes.reduce((latest, current) => {
                    return (current.id > latest.id) ? current : latest;
                }, userResumes[0]);
                
                console.log('Using latest resume:', latestResume);
                
                // Step 4: Submit application automatically
                await submitApplication(latestResume.id, jobData);
                
            } catch (error) {
                console.error('Failed to initialize application:', error);
                setError('Failed to load job details');
                setIsLoading(false);
            }
        };

        initializeAndApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, user, loading]);

    // AuthGuard now handles authentication, so this check is no longer needed
    // User will be redirected by AuthGuard if not authenticated

    if (!loading && !hasCompletedPersonalInfo(user)) {
        return (
            <>
                <PersonalInfoForm nextPage={window.location.href} />
                <ProfileCompletionModal 
                    open={true} 
                    onClose={() => {}}
                    redirectUrl={`/opportunities/${id}/apply`}
                />
            </>
        );
    }

    // Show no resume error
    if (noResumeError) {
        return (
            <Container maxWidth="md" sx={{ mt: 5 }}>
                {jobDetails && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {jobDetails.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {jobDetails.company} • {jobDetails.location}
                        </Typography>
                    </Box>
                )}
                <Card elevation={3}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Alert
                            severity="warning"
                            variant="filled"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                fontSize: '0.95rem',
                                fontWeight: 500,
                            }}
                        >
                            No resume found in your profile. Please upload a resume first.
                        </Alert>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            You need to have at least one resume uploaded to apply for jobs.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            href="/profile"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 4,
                                py: 1.5
                            }}
                        >
                            Go to Profile to Upload Resume
                        </Button>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    if (loading || isLoading) {
        return (
            <Container maxWidth="md" sx={{ mt: 5 }}>
                {jobDetails && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {jobDetails.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {jobDetails.company} • {jobDetails.location}
                        </Typography>
                    </Box>
                )}
                <Card elevation={3}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress 
                            size={60} 
                            sx={{ 
                                color: 'primary.main', 
                                mb: 3,
                            }} 
                        />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Preparing Your Application
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {loadingMessage}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please wait while we process your application...
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    if (isAnalyzing) {
        return (
            <Container maxWidth="md" sx={{ mt: 5 }}>
                {/* Job Title Header */}
                {jobDetails && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {jobDetails.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {jobDetails.company} • {jobDetails.location}
                        </Typography>
                    </Box>
                )}
                <Card elevation={3}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress 
                            size={60} 
                            sx={{ 
                                color: 'primary.main', 
                                mb: 3,
                                animation: 'pulse 2s infinite'
                            }} 
                        />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Analyzing and Submitting Your Application
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            We're analyzing your resume against the job requirements...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This may take a few moments. Please don't close this page.
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    if (alreadyApplied) {
        return (
            <Container maxWidth="md" sx={{ mt: 5 }}>
                {/* Job Title Header */}
                {jobDetails && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {jobDetails.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {jobDetails.company} • {jobDetails.location}
                        </Typography>
                    </Box>
                )}
                <Card elevation={3}>
                    <CardContent>
                        <Alert
                            severity="info"
                            variant="filled"
                            sx={{
                                mb: 2,
                                borderRadius: 2,
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                '& .MuiAlert-icon': {
                                    fontSize: '1.2rem'
                                }
                            }}
                        >
                            You have already applied to this job.
                        </Alert>
                    </CardContent>
                </Card>
            </Container>
        )
    }

    if (submitted) {
        // Process gaps data like in Resumes.js
        let gaps = [];
        if (rankingResults?.gaps_compared_to_target_job) {
            const gapsData = rankingResults.gaps_compared_to_target_job;
            
            if (Array.isArray(gapsData)) {
                gaps = gapsData;
            } else if (typeof gapsData === 'string') {
                if (gapsData.trim().startsWith('[') && gapsData.trim().endsWith(']')) {
                    try {
                        const parsedGaps = JSON.parse(gapsData);
                        if (Array.isArray(parsedGaps)) {
                            gaps = parsedGaps;
                        } else {
                            throw new Error('Not an array after parsing');
                        }
                    } catch (error) {
                        console.warn('Failed to parse stringified array, treating as plain text:', error);
                        const cleaned = gapsData.replace(/^\[|\]$/g, '').replace(/['"]/g, '');
                        gaps = cleaned
                            .split(/[,;•\n]/)
                            .map(gap => gap.trim())
                            .filter(gap => gap.length > 0);
                    }
                } else {
                    gaps = gapsData
                        .split(/[•\n]/)
                        .map(gap => gap.trim())
                        .filter(gap => gap.length > 0);
                    if (gaps.length === 0) {
                        gaps = [gapsData.trim()];
                    }
                }
            }
        }

        return (
            <Container maxWidth="md" sx={{ mt: 5 }}>
                {/* Job Title Header */}
                {jobDetails && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {jobDetails.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {jobDetails.company} • {jobDetails.location}
                        </Typography>
                    </Box>
                )}
                <Card elevation={3} sx={{ mb: 3 }}>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'success.main', mb: 3 }}>
                            🎉 Application Submitted Successfully!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            We've analyzed your resume against the job requirements. Here are your results:
                        </Typography>
                    </CardContent>
                </Card>

                {/* Resume Analysis Results */}
                {rankingResults && (
                    <Card elevation={3}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ mb: 3, textAlign: 'center' }}>
                                📊 Resume Analysis Results
                            </Typography>

                            {/* Grade and Score Display using Universal Component */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                                <GradeScoreDisplay 
                                    grade={rankingResults.grade} 
                                    score={rankingResults.raw_score}
                                    size="large"
                                    showLabels={true}
                                />
                            </Box>

                            {/* Analysis Summary */}
                            {rankingResults.justification && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ mb: 2 }}>
                                        📝 Analysis Summary
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                        lineHeight: 1.6,
                                        color: 'text.secondary',
                                        backgroundColor: '#f8f9fa',
                                        p: 3,
                                        borderRadius: 2,
                                        border: '1px solid #e9ecef'
                                    }}>
                                        {rankingResults.justification}
                                    </Typography>
                                </Box>
                            )}

                            {/* Areas for Improvement */}
                            {gaps.length > 0 && (
                                <Box>
                                    <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ mb: 2 }}>
                                        🎯 Areas for Improvement
                                    </Typography>
                                    <Box sx={{ 
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffeaa7',
                                        borderRadius: 2,
                                        p: 3
                                    }}>
                                        <Box component="ul" sx={{ 
                                            pl: 2, 
                                            margin: 0,
                                            '& li': {
                                                fontSize: '0.95rem',
                                                lineHeight: 1.6,
                                                color: 'text.secondary',
                                                mb: 1,
                                                listStyleType: 'disc'
                                            }
                                        }}>
                                            {gaps.map((gap, gapIndex) => (
                                                <li key={gapIndex}>{gap}</li>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {/* Call to Action */}
                            <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '1px solid #e9ecef' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Want to apply to more jobs? Visit the jobs page to explore new opportunities.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    href="/opportunities"
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1
                                    }}
                                >
                                    View All Opportunities
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Container>
        );
    }

    // Fallback - should rarely reach here as auto-submission happens
    return (
        <Container maxWidth="md" sx={{ mt: 5 }}>
            {jobDetails && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {jobDetails.title}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        {jobDetails.company} • {jobDetails.location}
                    </Typography>
                </Box>
            )}
            <Card elevation={3}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress 
                        size={60} 
                        sx={{ 
                            color: 'primary.main', 
                            mb: 3,
                        }} 
                    />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Processing...
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Please wait while we prepare your application.
                    </Typography>
                </CardContent>
            </Card>
        </Container>
    );
}

export default JobApply;